import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { trackEvent } from "@lib/track-event";
import { useStateMachine } from "little-state-machine";
import { ProductJsonLd } from "next-seo";
import { useRouter } from "next/router";
import { Spinner } from "@components/Spinner";
import { Recommendations } from "@components/Product/Recommendations";
import { getColor } from "@components/Product/lib/product-utils";
import { useRating } from "@components/ProductCard/hooks/useRating";
import { useCurrencyState } from "@hooks/useCurrency";
import { Gallery } from "../MediaGallery";
import { Sidebar } from "./Sidebar";
import { updateAction } from "./updateAction";
import { useVariants } from "./hooks/useVariants";
import { useMobileDetect } from "@hooks/index";
import { StyledWith } from "../StyledWith";
import { resolvePrice } from "../lib/format-variant-price";
import styles from "./styles/section.module.css";

export function Section({ product, settings, openReviewsDrawer, scrollToReviews }) {
  const [variant, setVariant] = useState({});
  const { currency } = useCurrencyState();
  const currentDevice = useMobileDetect();
  const { locale } = useRouter();
  const isMobile = currentDevice.isMobile();
  const { isError, error, isLoading, images } = useVariants(product);
  const { state } = useStateMachine({ updateAction });
  const styledWith = state["product"]?.styledWith;
  const rating = useRating(state["product"]?.productReferenceV2, state[`productGroup`]);

  const isFrench = locale === "fr";

  useEffect(() => {
    if (state["variant"] && (variant?.sku !== state["variant"]?.sku)) {
      setVariant(state["variant"].sku);
    }
  }, [state]);
  
  useEffect(() => {
    trackEvent("view_item", { product, productGroup: state[`productGroup`], variant: state["variant"] });
  }, [variant]);

  if (isLoading) {
    return (
      <section className={styles.loading}>
        <Spinner />
      </section>
    );
  }

  if (isError) {
    return <section className={styles.loading}>Something went wrong {error}</section>;
  }

  if (!state[`productGroup`]) {
    return (
      <section className={styles.loading}>
        <Spinner />
      </section>
    );
  }

  const colour = getColor(state["variant"]);
  const { price } = resolvePrice(state["variant"], currency);
  
  return (
    <>
      {/*
          THIS JUST DROPS A TAG ON THE PAGE DEPICT.AI CAN CRAWL
          TO GET THE SKU FOR THE CURRENT PRODUCT BEING VIEWED
         */}
      <meta name="depict-product-page-sku" content={state["variant"].sku} />
      {/* END */}
      {state["product"] && (
        <ProductJsonLd
          sku={state["variant"].sku}
          productName={state["product"].productReferenceV2?.store?.title}
          description={state["product"].productReferenceV2?.description}
          images={images.map(({ image }) => image?.originalSrc)}
          brand="Kotn"
          color={colour}
          aggregateRating={{
            ratingValue: rating?.average,
            reviewCount: rating?.count,
          }}
          offers={[
            {
              price: price.amount,
              priceCurrency: currency,
              itemCondition: "https://schema.org/NewCondition",
              availability: state["variant"].availableForSale
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            },
          ]}
        />
      )}
      <section className={styles.container}>
        <Gallery media={images} title={state[`productGroup`].title} />
        <Sidebar
          settings={settings}
          openReviewsDrawer={openReviewsDrawer}
          scrollToReviews={scrollToReviews}
        />
      </section>
      {styledWith && isMobile && !isFrench && locale !== 'gb' && (
        <StyledWith customTitle={styledWith.customTitle} products={styledWith.products} isGrid />
      )}
      <Recommendations variant_ids={state["variant"].sku} type="related" />
    </>
  );
}

Section.propTypes = {
  product: PropTypes.object.isRequired,
  openReviewsDrawer: PropTypes.func,
  settings: PropTypes.object,
};
